import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  EventSeriesView,
  EventView,
  PageResponse,
  SeriesRescheduleView,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import {
  addCalendarDays,
  canSubmitReschedule,
  describeSeries,
  eventStatusKeys,
  formatEventWhen,
  intervalWeekChoices,
  isTogglableOccurrence,
  localDayOf,
  rescheduleFormOf,
  rescheduleRequestBody,
  toggled,
  weekdayKeys,
  type RescheduleForm,
} from './scheduling';

export interface SeriesPanelProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  csrfToken: string;
  seriesId: string;
  /** The occurrence the panel is opened from; it is highlighted in the list. */
  currentEventId: string;
  onChanged: () => void;
}

type ErrorKey = ApiRequestError['messageKey'];

interface Failure {
  key: ErrorKey;
  code: string;
}

function failureOf(error: unknown): Failure {
  return error instanceof ApiRequestError
    ? { key: error.messageKey, code: error.code }
    : { key: 'error.unexpected', code: 'UNEXPECTED' };
}

/** How far ahead the list of occurrences reaches. */
const upcomingDays = 120;

/** An occurrence being switched off (cancelled) or on again (restored), waiting for confirmation. */
interface PendingToggle {
  eventId: string;
  action: 'cancel' | 'restore';
  reason: string;
  notifyParticipants: boolean;
}

/**
 * A recurring series at a glance: its rhythm, its next occurrences with a tick box each (untick to
 * cancel one session, tick to bring it back) and a form that changes the rhythm from a date on.
 */
export function SeriesPanel({
  locale,
  base,
  csrfToken,
  seriesId,
  currentEventId,
  onChanged,
}: SeriesPanelProps): React.JSX.Element {
  const [series, setSeries] = useState<EventSeriesView | null>(null);
  const [occurrences, setOccurrences] = useState<EventView[]>([]);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingToggle | null>(null);
  const [form, setForm] = useState<RescheduleForm | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const today = localDayOf(new Date());
    try {
      const [loadedSeries, page] = await Promise.all([
        apiRequest<EventSeriesView>(`${base}/event-series/${seriesId}`),
        apiRequest<PageResponse<EventView>>(
          `${base}/events?${new URLSearchParams({
            seriesId,
            fromDate: today,
            toDate: addCalendarDays(today, upcomingDays),
            limit: '100',
          }).toString()}`,
        ),
      ]);
      setSeries(loadedSeries);
      setOccurrences(page.items);
    } catch (error) {
      setFailure(failureOf(error));
    }
  }, [base, seriesId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<string | null>): Promise<void> {
    setBusy(true);
    setFailure(null);
    setNotice(null);
    try {
      setNotice(await work());
      setPending(null);
      onChanged();
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setBusy(false);
    }
    await load();
  }

  async function confirmToggle(confirmAthleteOverlaps: boolean): Promise<void> {
    if (pending === null) return;
    const occurrence = occurrences.find((candidate) => candidate.id === pending.eventId);
    if (occurrence === undefined) return;
    await run(async () => {
      if (pending.action === 'cancel') {
        await apiRequest<EventView>(`${base}/events/${occurrence.id}/cancel`, {
          method: 'POST',
          body: {
            reason: pending.reason.trim(),
            scope: 'this',
            notifyParticipants: pending.notifyParticipants,
          },
          csrfToken,
          ifMatchVersion: occurrence.version,
        });
        return translate(locale, 'calendar.series.cancelledOne');
      }
      await apiRequest<EventView>(`${base}/events/${occurrence.id}/restore`, {
        method: 'POST',
        body: { notifyParticipants: pending.notifyParticipants, confirmAthleteOverlaps },
        csrfToken,
        ifMatchVersion: occurrence.version,
      });
      return translate(locale, 'calendar.series.restoredOne');
    });
  }

  async function reschedule(): Promise<void> {
    if (series === null || form === null) return;
    await run(async () => {
      const result = await apiRequest<SeriesRescheduleView>(`${base}/event-series/${series.id}`, {
        method: 'PATCH',
        body: rescheduleRequestBody(form),
        csrfToken,
        ifMatchVersion: series.version,
      });
      setForm(null);
      return translate(locale, 'calendar.series.rescheduled')
        .replace('{kept}', String(result.kept))
        .replace('{cancelled}', String(result.cancelled))
        .replace('{created}', String(result.created));
    });
  }

  if (series === null) {
    return failure === null ? (
      <Typography sx={{ mt: 2 }}>{translate(locale, 'calendar.loading')}</Typography>
    ) : (
      <Alert severity="error" sx={{ mt: 2 }}>
        {translate(locale, failure.key)}
      </Alert>
    );
  }

  const now = new Date();
  const today = localDayOf(now);
  const nextOpen = occurrences.find(
    (occurrence) => occurrence.status !== 'cancelled' && isTogglableOccurrence(occurrence, now),
  );

  const notifyBox = (checked: boolean, onToggle: (checked: boolean) => void): React.JSX.Element => (
    <FormControlLabel
      label={translate(locale, 'calendar.series.notify')}
      control={
        <Checkbox
          checked={checked}
          onChange={(event) => {
            onToggle(event.target.checked);
          }}
        />
      }
    />
  );

  return (
    <Box
      component="section"
      aria-label={translate(locale, 'calendar.series.title')}
      sx={{ mt: 2, borderTop: 1, borderColor: 'divider', pt: 2 }}
    >
      <Typography variant="subtitle1" component="h4" sx={{ fontWeight: 600 }}>
        {translate(locale, 'calendar.series.title')}
      </Typography>
      <Typography>{describeSeries(series, locale)}</Typography>
      <Typography variant="body2" color="text.secondary">
        {series.untilOn === null
          ? translate(locale, 'calendar.series.noEnd')
          : translate(locale, 'calendar.series.until').replace(
              '{date}',
              formatCalendarDate(series.untilOn, locale),
            )}
      </Typography>
      {series.status === 'ended' && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {translate(locale, 'calendar.series.ended')}
        </Alert>
      )}
      {failure !== null && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {translate(locale, failure.key)}
        </Alert>
      )}
      {notice !== null && failure === null && (
        <Alert severity="success" sx={{ mt: 1 }}>
          {notice}
        </Alert>
      )}

      <Typography variant="body2" sx={{ fontWeight: 600, mt: 2 }}>
        {translate(locale, 'calendar.series.upcoming')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {translate(locale, 'calendar.series.upcomingHint')}
      </Typography>
      {occurrences.length === 0 && (
        <Typography color="text.secondary">{translate(locale, 'calendar.series.none')}</Typography>
      )}
      <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {occurrences.map((occurrence) => {
          const takesPlace = occurrence.status !== 'cancelled';
          const togglable = isTogglableOccurrence(occurrence, now) && series.status === 'active';
          const confirming = pending?.eventId === occurrence.id;
          return (
            <Box component="li" key={occurrence.id} sx={{ py: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  label={
                    <Typography
                      sx={{
                        fontWeight: occurrence.id === currentEventId ? 600 : 400,
                        textDecoration: takesPlace ? 'none' : 'line-through',
                      }}
                    >
                      {formatEventWhen(occurrence, locale)}
                    </Typography>
                  }
                  control={
                    <Checkbox
                      checked={takesPlace}
                      disabled={!togglable || busy}
                      onChange={() => {
                        setFailure(null);
                        setPending({
                          eventId: occurrence.id,
                          action: takesPlace ? 'cancel' : 'restore',
                          reason: translate(locale, 'calendar.series.defaultCancelReason'),
                          notifyParticipants: true,
                        });
                      }}
                    />
                  }
                />
                <Chip
                  size="small"
                  color={
                    occurrence.status === 'published'
                      ? 'success'
                      : occurrence.status === 'cancelled'
                        ? 'error'
                        : 'default'
                  }
                  label={translate(locale, eventStatusKeys[occurrence.status])}
                />
                {occurrence.isException && (
                  <Typography variant="body2" color="text.secondary">
                    {translate(locale, 'calendar.series.ownTime')}
                  </Typography>
                )}
                {!takesPlace && occurrence.cancelReason !== null && (
                  <Typography variant="body2" color="text.secondary">
                    {occurrence.cancelReason}
                  </Typography>
                )}
              </Box>
              {confirming && (
                <Box
                  component="form"
                  sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', pl: 4 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    void confirmToggle(false);
                  }}
                >
                  {pending.action === 'cancel' && (
                    <TextField
                      size="small"
                      required
                      sx={{ minWidth: 260, flexGrow: 1 }}
                      label={translate(locale, 'calendar.action.cancelReason')}
                      value={pending.reason}
                      onChange={(event) => {
                        setPending({ ...pending, reason: event.target.value });
                      }}
                    />
                  )}
                  {occurrence.publishedAt !== null
                    ? notifyBox(pending.notifyParticipants, (checked) => {
                        setPending({ ...pending, notifyParticipants: checked });
                      })
                    : null}
                  <Button
                    type="submit"
                    variant="contained"
                    color={pending.action === 'cancel' ? 'error' : 'primary'}
                    sx={{ minHeight: 44 }}
                    disabled={busy || (pending.action === 'cancel' && pending.reason.trim() === '')}
                  >
                    {translate(
                      locale,
                      pending.action === 'cancel'
                        ? 'calendar.series.confirmCancel'
                        : 'calendar.series.confirmRestore',
                    )}
                  </Button>
                  {pending.action === 'restore' && failure?.code === 'ATHLETE_OVERLAP' && (
                    <Button
                      variant="outlined"
                      sx={{ minHeight: 44 }}
                      disabled={busy}
                      onClick={() => {
                        void confirmToggle(true);
                      }}
                    >
                      {translate(locale, 'calendar.series.restoreAnyway')}
                    </Button>
                  )}
                  <Button
                    sx={{ minHeight: 44 }}
                    onClick={() => {
                      setPending(null);
                      setFailure(null);
                    }}
                  >
                    {translate(locale, 'calendar.series.keep')}
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {series.status === 'active' && form === null && (
        <Button
          variant="outlined"
          sx={{ mt: 2, minHeight: 44 }}
          onClick={() => {
            setFailure(null);
            setNotice(null);
            setForm(
              rescheduleFormOf(
                series,
                nextOpen?.date ?? today,
                translate(locale, 'calendar.series.defaultRescheduleReason'),
              ),
            );
          }}
        >
          {translate(locale, 'calendar.series.change')}
        </Button>
      )}
      {form !== null && (
        <Box
          component="form"
          aria-label={translate(locale, 'calendar.series.change')}
          sx={{ mt: 2 }}
          onSubmit={(event) => {
            event.preventDefault();
            void reschedule();
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {translate(locale, 'calendar.series.changeHint')}
          </Typography>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                type="date"
                required
                label={translate(locale, 'calendar.series.fromDate')}
                value={form.fromDate}
                onChange={(event) => {
                  setForm({ ...form, fromDate: event.target.value });
                }}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: today } }}
              />
              <TextField
                select
                size="small"
                sx={{ minWidth: 200 }}
                label={translate(locale, 'calendar.series.interval')}
                value={form.intervalWeeks}
                onChange={(event) => {
                  setForm({ ...form, intervalWeeks: Number(event.target.value) });
                }}
              >
                {intervalWeekChoices.map((weeks) => (
                  <MenuItem key={weeks} value={weeks}>
                    {weeks === 1
                      ? translate(locale, 'calendar.series.intervalOne')
                      : translate(locale, 'calendar.series.intervalMany').replace(
                          '{weeks}',
                          String(weeks),
                        )}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="time"
                required
                label={translate(locale, 'calendar.field.startTime')}
                value={form.startTime}
                onChange={(event) => {
                  setForm({ ...form, startTime: event.target.value });
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                size="small"
                type="time"
                required
                label={translate(locale, 'calendar.field.endTime')}
                value={form.endTime}
                onChange={(event) => {
                  setForm({ ...form, endTime: event.target.value });
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                size="small"
                type="date"
                label={translate(locale, 'calendar.create.until')}
                value={form.until}
                onChange={(event) => {
                  setForm({ ...form, until: event.target.value });
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {translate(locale, 'calendar.create.weekdays')}
              </Typography>
              <FormGroup row>
                {[1, 2, 3, 4, 5, 6, 7].map((weekday) => (
                  <FormControlLabel
                    key={weekday}
                    label={translate(locale, weekdayKeys[weekday] ?? 'calendar.weekday.1')}
                    control={
                      <Checkbox
                        checked={form.weekdays.includes(weekday)}
                        onChange={() => {
                          setForm({ ...form, weekdays: toggled(form.weekdays, weekday) });
                        }}
                      />
                    }
                  />
                ))}
              </FormGroup>
            </Box>
            <TextField
              size="small"
              required
              label={translate(locale, 'calendar.series.removedReason')}
              value={form.reason}
              onChange={(event) => {
                setForm({ ...form, reason: event.target.value });
              }}
            />
            {notifyBox(form.notifyParticipants, (checked) => {
              setForm({ ...form, notifyParticipants: checked });
            })}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                type="submit"
                variant="contained"
                sx={{ minHeight: 44 }}
                disabled={busy || !canSubmitReschedule(form, today)}
              >
                {translate(locale, 'calendar.series.saveChange')}
              </Button>
              <Button
                sx={{ minHeight: 44 }}
                onClick={() => {
                  setForm(null);
                }}
              >
                {translate(locale, 'calendar.action.discard')}
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
